import { FormType } from './patternSuperGroups';

export type ViewMode = 'patterns' | 'forms' | 'families' | 'levels';

export type Selection =
  | { kind: 'pattern'; value: string }
  | { kind: 'form'; value: FormType }
  | { kind: 'base'; value: string }
  | { kind: 'level'; value: string };

export interface BrowseState {
  view: ViewMode;
  selection: Selection | null;
}

const VALID_VIEWS: ReadonlySet<ViewMode> = new Set(['patterns', 'forms', 'families', 'levels']);
const VALID_FORMS: ReadonlySet<FormType> = new Set(['AAA', 'ABB', 'ABA', 'ABC']);
const VALID_LEVELS: ReadonlySet<string> = new Set(['A1', 'A2', 'B1', 'B2', 'C1', 'C2']);

function isViewMode(value: string | null): value is ViewMode {
  return value !== null && VALID_VIEWS.has(value as ViewMode);
}

function isFormType(value: string | null): value is FormType {
  return value !== null && VALID_FORMS.has(value as FormType);
}

function isLevel(value: string | null): value is string {
  return value !== null && VALID_LEVELS.has(value);
}

export function parseBrowseUrl(params: URLSearchParams): BrowseState {
  const rawView = params.get('view');
  const view: ViewMode = isViewMode(rawView) ? rawView : 'levels';

  if (view === 'patterns') {
    const pattern = params.get('pattern');
    if (pattern && pattern.length > 0) {
      return { view, selection: { kind: 'pattern', value: pattern } };
    }
    return { view, selection: null };
  }

  if (view === 'forms') {
    const rawForm = params.get('form');
    if (isFormType(rawForm)) {
      return { view, selection: { kind: 'form', value: rawForm } };
    }
    return { view, selection: null };
  }

  if (view === 'families') {
    const base = params.get('base');
    if (base && base.length > 0) {
      return { view, selection: { kind: 'base', value: base } };
    }
    return { view, selection: null };
  }

  // view === 'levels'
  const rawLevel = params.get('level');
  if (isLevel(rawLevel)) {
    return { view, selection: { kind: 'level', value: rawLevel } };
  }
  return { view, selection: null };
}

export function buildBrowseSearch(state: BrowseState): string {
  // The "default landing" — levels view with no drill — collapses to an empty query string.
  if (state.view === 'levels' && state.selection === null) {
    return '';
  }

  const params = new URLSearchParams();
  params.set('view', state.view);

  if (state.selection) {
    switch (state.selection.kind) {
      case 'pattern':
        params.set('pattern', state.selection.value);
        break;
      case 'form':
        params.set('form', state.selection.value);
        break;
      case 'base':
        params.set('base', state.selection.value);
        break;
      case 'level':
        params.set('level', state.selection.value);
        break;
    }
  }

  return params.toString();
}
